// react
import { ChangeEvent, MouseEvent, ReactNode, useMemo, useState } from 'react';

// types
import { Order, Pagination, Sort } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import {
	Alert,
	Box,
	BoxProps,
	Checkbox,
	Grid,
	InputLabel,
	SxProps,
	Table as MuiTable,
	TableBody,
	TableCell,
	TableFooter as MuiTableFooter,
	TableHead,
	TableRow as MuiTableRow,
	Typography,
	Tooltip,
	TooltipProps,
} from '@mui/material';

// ui
import { Empty, EmptyProps } from '../Empty/Empty';
import { TableFooter, TableFooterProps } from '../TableFooter/TableFooter';
import { FnSort } from '../TableHeaders/TableHeaders';
import { TableRow, TableRowBgColor } from './TableRow/TableRow';

// theme
import { theme } from '../../../theme';

// icons
import {
	InfoOutlined as IconInfo,
	KeyboardArrowDownOutlined as IconKeyboardArrowDown,
	KeyboardArrowUpOutlined as IconKeyboardArrowUp,
} from '@mui/icons-material';

// props
export const tableHeaderPositions = ['center', 'left', 'right'];

export type TableHeaderPosition = (typeof tableHeaderPositions)[number];

export interface TableHeader {
	align?: TableHeaderPosition;
	checked?: boolean;
	CheckboxProps?: {
		checked: boolean;
		disabled?: boolean;
		onChange: (e: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
	};
	isSortable?: boolean;
	key?: string;
	label?: ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	render?: (value: any, header: TableHeader, data: any) => ReactNode;
	sx?: SxProps;
	TooltipProps?: Omit<TooltipProps, 'children'>;
	value: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TableData = any;

export interface TableProps {
	borderRadius?: number;
	BoxFooterProps?: BoxProps;
	btnExpandPlacement?: 'end' | 'start';
	children?: ReactNode;
	color?: 'blue' | 'grey';
	colorBorder?: 'blue' | 'grey';
	data: TableData[];
	EmptyProps?: EmptyProps;
	frame?: 'CARD' | 'DEFAULT' | 'DIALOG';
	getRowBgColor?: (item: TableData, index: number) => TableRowBgColor;
	hasBorder?: boolean;
	hasDividers?: boolean;
	hasStripes?: boolean;
	headers: TableHeader[];
	isCondensed?: boolean;
	isLoading?: boolean;
	isStacked?: boolean;
	isSticky?: boolean;
	onSort?: FnSort;
	pagination?: Pagination;
	sxBox?: SxProps;
	sxTable?: SxProps;
	sxTd?: SxProps;
	TableFooterProps?: TableFooterProps;
}

export const Table = ({
	borderRadius,
	BoxFooterProps,
	btnExpandPlacement = 'end',
	children,
	color = 'blue',
	colorBorder = 'grey',
	data = [],
	EmptyProps,
	frame = 'DEFAULT',
	getRowBgColor,
	hasStripes = false,
	hasBorder = false,
	hasDividers = false,
	headers,
	isCondensed = false,
	isLoading,
	isStacked = false,
	isSticky = false,
	onSort,
	pagination,
	sxBox = {},
	sxTable = {},
	sxTd = {},
	TableFooterProps,
}: TableProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// state
	const [activeSort, setActiveSort] = useState<Sort | undefined>(undefined);
	const tableId = useMemo(() => crypto.randomUUID(), []);

	// vars
	let cellPadding = theme.spacing(2);

	if (isCondensed) {
		cellPadding = theme.spacing(1, 2);
	}

	const isExpandable = data.some((item) => item.children || item.expandedContent);

	if (frame === 'CARD') {
		cellPadding = theme.spacing(1.5, 3);
	}

	const colors = {
		blue: {
			active: theme.palette.brand.blue[400],
			background: theme.palette.brand.blue[100],
			border: theme.palette.brand.blue[500],
			header: theme.palette.brand.blue[700],
			inactive: theme.palette.brand.blue[700],
		},
		grey: {
			active: theme.palette.brand.grey[800],
			background: theme.palette.brand.grey[100],
			border: theme.palette.brand.grey[400],
			header: theme.palette.brand.grey[800],
			inactive: theme.palette.brand.grey[400],
		},
	};

	const sxHeader: SxProps = {
		borderBottom: hasBorder ? `1px ${colors[colorBorder].border} solid` : null,
		color: colors[color].header,
		padding: cellPadding,
		textAlign: 'left',
		whiteSpace: 'nowrap',
	};

	const handleSort = (e: MouseEvent<HTMLButtonElement>, value: TableHeader['value']) => {
		// define new order
		const orderNew: Order = activeSort?.order === 'ASC' ? 'DESC' : 'ASC';

		// run callback
		if (onSort) {
			onSort({ e, order: orderNew, value });
		}

		// set active sorted header
		if (setActiveSort) {
			setActiveSort({
				order: orderNew,
				value,
			});
		}

		// set search params
		searchParams.set('orderBy', value);
		searchParams.set('order', orderNew);
		setSearchParams(searchParams);
	};

	const renderTable = () => {
		let cellExpandBtn: ReactNode = null;

		if (isExpandable) {
			cellExpandBtn = (
				<TableCell sx={{ ...sxHeader, textAlign: 'right', width: 0, minWidth: 'fit-content' }} />
			);
		}

		const thead = (
			<TableHead
				sx={(theme) => {
					let sx: SxProps = {
						backgroundColor: colors[color].background,
					};

					if (isStacked) {
						sx = {
							...sx,
							display: 'none',
							[theme.breakpoints.up('lg')]: {
								display: 'table-header-group',
							},
						};
					}

					if (isSticky) {
						sx = {
							...sx,
							position: 'sticky',
							top: 0,
						};
					}

					return sx;
				}}>
				<MuiTableRow>
					{btnExpandPlacement === 'start' && cellExpandBtn}
					{headers.map((header) => {
						const isSortActive = header.value === activeSort?.value;
						let justifyContent = 'flex-start';
						if (header.align === 'center') {
							justifyContent = 'center';
						} else if (header.align === 'right') {
							justifyContent = 'flex-end';
						}

						let thTooltipIcon: ReactNode = null;

						if (header.TooltipProps) {
							thTooltipIcon = (
								<Tooltip arrow={true} placement="top" {...header.TooltipProps}>
									<IconInfo
										color="inherit"
										sx={{ color: theme.palette.brand.grey[500], cursor: 'pointer' }}
									/>
								</Tooltip>
							);
						}

						const th = (
							<Grid
								alignItems="center"
								container
								justifyContent={justifyContent}
								spacing={0.5}
								wrap="nowrap">
								<Grid item>
									<Grid alignItems="center" container spacing={1}>
										{header.CheckboxProps ? (
											<>
												<Grid item>
													<Checkbox
														checked={Boolean(header.CheckboxProps?.checked ?? false)}
														disabled={Boolean(header.CheckboxProps?.disabled ?? false)}
														id={`select-all-${tableId}-${header.value}`}
														onChange={(e) => header.CheckboxProps?.onChange(e, e.target.checked)}
														sx={{ p: 0 }}
													/>
												</Grid>
												<Grid item>
													<InputLabel
														htmlFor={`select-all-${tableId}-${header.value}`}
														sx={(theme) => ({
															...theme.typography.caption,
															fontWeight: 600,
															m: 0,
														})}>
														{header.label}
													</InputLabel>
												</Grid>
											</>
										) : (
											<Grid item>
												<Typography fontWeight={600} variant="caption">
													{header.label}
												</Typography>
											</Grid>
										)}
									</Grid>
								</Grid>
								{thTooltipIcon && <Grid item>{thTooltipIcon}</Grid>}
								{header.isSortable && (
									<Grid item>
										<Grid
											container
											sx={(theme) => {
												return {
													'& svg': {
														transition: 'all 200ms',
														display: 'block !important',
														width: `${theme.spacing(2)} !important`,
														height: 'auto !important',
													},
												};
											}}>
											<Grid
												item
												xs={12}
												sx={(theme) => {
													return {
														'& svg': {
															color:
																isSortActive && activeSort?.order === 'ASC'
																	? colors[color].active
																	: colors[color].inactive,
														},
													};
												}}>
												<IconKeyboardArrowUp />
											</Grid>
											<Grid
												item
												xs={12}
												sx={(theme) => {
													return {
														'& svg': {
															color:
																isSortActive && activeSort?.order === 'DESC'
																	? colors[color].active
																	: colors[color].inactive,
														},
													};
												}}>
												<IconKeyboardArrowDown />
											</Grid>
										</Grid>
									</Grid>
								)}
							</Grid>
						);

						const thButton = (
							<Box
								component="button"
								onClick={(e: MouseEvent<HTMLButtonElement>) => handleSort(e, header.value)}
								sx={(theme) => {
									return {
										color: colors[color].header,
										'&:hover': {
											'& svg': {
												opacity: 1,
											},
										},
									};
								}}>
								{th}
								{thTooltipIcon}
							</Box>
						);

						return (
							<TableCell
								key={`table-header-${header.value}-${header.label}`}
								sx={(theme) => {
									let sx: SxProps = {
										...sxHeader,
										...(header.sx as object),
									};

									if (header.align) {
										sx = {
											...sx,
											textAlign: {
												lg: header.align,
											},
										};
									}

									return sx;
								}}>
								{header.isSortable ? thButton : th}
							</TableCell>
						);
					})}
					{btnExpandPlacement === 'end' && cellExpandBtn}
				</MuiTableRow>
			</TableHead>
		);

		let tbodyContent: ReactNode = <Alert severity="info">{t('alert.empty')}</Alert>;

		if (isLoading) {
			tbodyContent = (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: theme.spacing(2, 0),
					}}>
					<Empty
						hasContent={false}
						hasTitle={false}
						isLoading={isLoading}
						sxBox={{ height: '100%' }}
						{...EmptyProps}
					/>
				</Box>
			);
		}

		let tbody: ReactNode = (
			<TableBody>
				<MuiTableRow>
					<TableCell
						colSpan={headers.length}
						sx={(theme) => {
							return {
								padding: theme.spacing(1),
								textAlign: 'center',
							};
						}}>
						{tbodyContent}
					</TableCell>
				</MuiTableRow>
			</TableBody>
		);

		if (data.length > 0 && !isLoading) {
			tbody = (
				<TableBody>
					{data.map((item: TableData, i: number) => {
						let bgColor: TableRowBgColor = 'transparent';

						// Use custom row color function if provided, otherwise fall back to stripes
						if (getRowBgColor) {
							bgColor = getRowBgColor(item, i);
						} else if (hasStripes && i % 2 !== 0) {
							bgColor = 'grey';
						}

						return (
							<TableRow
								bgColor={bgColor}
								btnExpandPlacement={btnExpandPlacement}
								frame={frame}
								hasDividers={hasDividers && i < data.length - 1}
								headers={headers}
								isExpandable={isExpandable}
								isStacked={isStacked}
								item={item}
								key={`table-row-${i}`}
								sxTd={sxTd}
							/>
						);
					})}
				</TableBody>
			);
		}

		let tfoot;

		let rowPagination: ReactNode = null;

		if (pagination) {
			let endNum = pagination.page * pagination.perPage;
			let startNum = endNum - pagination.perPage + 1;

			if (startNum === 0) {
				startNum = 1;
			}

			if (pagination.total && endNum > pagination.total) {
				endNum = pagination.total;
			}

			if (pagination.total === 0) {
				endNum = 0;
				startNum = 0;
			}

			rowPagination = (
				<MuiTableRow
					sx={{
						backgroundColor: colors.grey.background,
					}}>
					<TableCell
						colSpan={headers.length}
						sx={(theme) => {
							return {
								padding: cellPadding,
								borderBottomLeftRadius: 8,
								borderBottomRightRadius: 8,
							};
						}}>
						<Typography color="brand.grey.500" variant="caption">
							{t('tableShowing', {
								end: endNum,
								start: startNum,
								total: pagination.total,
							})}
						</Typography>
					</TableCell>
				</MuiTableRow>
			);
		}

		if (rowPagination) {
			tfoot = <MuiTableFooter>{rowPagination}</MuiTableFooter>;
		}

		return (
			<MuiTable
				sx={(theme) => {
					return {
						borderCollapse: 'collapse',
						width: '100%',
						height: isLoading ? '100%' : 'auto',
						...(sxTable as object),
					};
				}}>
				{thead}
				{tbody}
				{children}
				{tfoot}
			</MuiTable>
		);
	};

	return (
		<Box
			sx={(theme) => {
				const sx: SxProps = {
					border: hasBorder ? `1px ${colors[colorBorder].border} solid` : null,
					borderRadius: borderRadius || null,
					overflowX: 'auto',
					height: '100%',
					...(sxBox as object),
				};

				return sx;
			}}>
			<Grid container flexDirection="column" justifyContent="space-between" sx={{ height: '100%' }}>
				<Grid flex={1} item>
					{renderTable()}
				</Grid>
				{TableFooterProps && (
					<Grid item>
						<Box
							sx={(theme) => {
								return {
									backgroundColor: colors[color].background,
									borderTopColor: colors[colorBorder].border,
									borderTopStyle: 'solid',
									borderTopWidth: 1,
									padding: cellPadding,
									...(BoxFooterProps?.sx as object),
								};
							}}>
							<TableFooter {...TableFooterProps} />
						</Box>
					</Grid>
				)}
			</Grid>
		</Box>
	);
};
